import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, User, ShieldAlert, LogOut, IndianRupee, Clock, Share2, History, CheckCircle, Trophy, Star, Wallet, Swords, Crown, Coffee } from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { getApiUrl } from '@/lib/utils';
import { AddToHomeScreen } from '@/components/AddToHomeScreen';
import { PushNotificationManager, NotificationPrompt } from '@/components/PushNotifications';
import PlayerStats from '@/components/PlayerStats';
import PWAInstallBanner from '@/components/PWAInstallBanner';

const API_URL = getApiUrl();

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [matches, setMatches] = useState([]);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'past'
  const [duesThreshold, setDuesThreshold] = useState(300);
  const [showDuesPopup, setShowDuesPopup] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, matchesRes, paymentsRes, walletRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/matches/dashboard`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/payments/user`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/users/wallet`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);

      setUser(userRes.data);
      setMatches(matchesRes.data);
      setPayments(paymentsRes.data);
      setBalance(walletRes.data.wallet_balance || 0);

      // Fetch dynamic dues threshold separately - falls back to default 300 if it fails
      try {
        const settingsRes = await axios.get(`${API_URL}/api/settings`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        });
        setDuesThreshold(settingsRes.data.rsvp_dues_threshold);
      } catch (settingsError) {
        console.error('Failed to load settings, using default threshold');
      }

      // Show dues pop-up if there are ANY pending dues and it hasn't been dismissed this session
      const pendingTotal = paymentsRes.data
        .filter(p => p.status === 'pending')
        .reduce((sum, p) => sum + p.amount, 0);
      if (pendingTotal > 0 && sessionStorage.getItem('duesPopupDismissed') !== 'true') {
        setShowDuesPopup(true);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      localStorage.removeItem('session_token');
      sessionStorage.removeItem('duesPopupDismissed');
      toast.success('Logged out');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const dismissDuesPopup = () => {
    setShowDuesPopup(false);
    sessionStorage.setItem('duesPopupDismissed', 'true');
  };

  // Include both pending and paid_pending_verification for aggregate card (only positive amounts)
  const pendingPayments = payments.filter(p => (p.status === 'pending' || p.status === 'paid_pending_verification') && p.amount > 0);
  const totalDue = pendingPayments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);

  // Check if user is a captain for any upcoming Sunday match
  const captainMatches = matches.filter(m => {
    if (m.match_type !== 'sunday') return false;
    if (!m.captains) return false;
    const matchDate = new Date(m.date);
    matchDate.setHours(6, 30, 0, 0); // Deadline is 6:30am on match day
    if (new Date() >= matchDate) return false; // Past deadline
    return m.captains.captain_a === user?.user_id || m.captains.captain_b === user?.user_id;
  });

  // Check for matches where user can spectate (has captains but user is not captain)
  const spectatorMatches = matches.filter(m => {
    if (m.match_type !== 'sunday') return false;
    if (!m.captains) return false;
    const matchDate = new Date(m.date);
    matchDate.setHours(6, 30, 0, 0);
    if (new Date() >= matchDate) return false;
    // User is not a captain but match has captains assigned
    return m.captains.captain_a !== user?.user_id && m.captains.captain_b !== user?.user_id;
  });

  // Filter matches by date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const upcomingMatches = matches.filter(m => {
    const matchDate = new Date(m.date);
    matchDate.setHours(0, 0, 0, 0);
    return matchDate >= today;
  });

  const pastMatches = matches.filter(m => {
    const matchDate = new Date(m.date);
    matchDate.setHours(0, 0, 0, 0);
    return matchDate < today;
  }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first

  // Get payments for past matches
  const getMatchPayment = (matchId) => {
    return payments.find(p => p.match_id === matchId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Pending Dues Pop-up - shows on login, dismissible, reappears until dues are cleared */}
      <Dialog open={showDuesPopup} onOpenChange={(open) => !open && dismissDuesPopup()}>
        <DialogContent className="max-w-sm" data-testid="dues-popup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <IndianRupee className="w-5 h-5" />
              Pending Dues
            </DialogTitle>
            <DialogDescription className="sr-only">You have pending dues that need to be cleared before you can RSVP for new matches</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            You have <span className="font-bold text-red-600">₹{totalDue.toFixed(0)}</span> in pending dues. Clear dues to RSVP for new matches.
          </p>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={dismissDuesPopup}
              className="flex-1"
              data-testid="dues-popup-dismiss-btn"
            >
              Later
            </Button>
            <Button
              onClick={() => { dismissDuesPopup(); navigate('/payments'); }}
              className="flex-1 bg-[#E63946] hover:bg-[#DC2626] text-white"
              data-testid="dues-popup-pay-now-btn"
            >
              Pay Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/images/weakenders-logo.png"
              alt="Weakenders"
              className="w-10 h-10"
            />
            <div>
              <h1 className="text-lg font-bold text-[#0F172A]">WEAKENDERS</h1>
              <p className="text-xs text-slate-600">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <PushNotificationManager />
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="icon"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Notification Prompt */}
        <NotificationPrompt />

        {/* PWA Install Banner - Above Awards */}
        <PWAInstallBanner />

        {/* Selection Arena Banner for Captains */}
        {captainMatches.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white">You're a Captain!</h3>
                <p className="text-white/80 text-sm">
                  Enter the Selection Arena to pick your team
                </p>
              </div>
              <Button
                onClick={() => navigate(`/arena/${captainMatches[0].match_id}`)}
                className="bg-white text-amber-600 hover:bg-white/90 font-bold"
                data-testid="enter-arena-btn"
              >
                <Swords className="w-4 h-4 mr-2" />
                Enter Arena
              </Button>
            </div>
          </motion.div>
        )}

        {/* Selection Arena Banner for Spectators */}
        {spectatorMatches.length > 0 && captainMatches.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl p-4 shadow-lg"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <Swords className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white">Team Selection Live!</h3>
                <p className="text-white/70 text-sm">
                  Watch the captains pick their teams
                </p>
              </div>
              <Button
                onClick={() => navigate(`/arena/${spectatorMatches[0].match_id}`)}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
                data-testid="watch-arena-btn"
              >
                Watch Live
              </Button>
            </div>
          </motion.div>
        )}

        {/* Player Stats & Badges - First Section */}
        <PlayerStats />
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            icon={<Calendar className="w-6 h-6 text-[#E63946]" />}
            label="Upcoming"
            value={upcomingMatches.length}
          />
          <StatCard
            icon={<IndianRupee className="w-6 h-6 text-[#E63946]" />}
            label="Total Due"
            value={`₹${totalDue.toFixed(0)}`}
            subtitle={balance > 0 ? `Wallet: ₹${balance.toFixed(0)}` : null}
          />
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'upcoming'
                ? 'bg-[#E63946] text-white'
                : 'text-slate-600 hover:text-[#E63946]'
            }`}
            data-testid="tab-upcoming"
          >
            <Calendar className="w-4 h-4" />
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'past'
                ? 'bg-[#E63946] text-white'
                : 'text-slate-600 hover:text-[#E63946]'
            }`}
            data-testid="tab-past"
          >
            <History className="w-4 h-4" />
            Past Games
          </button>
        </div>

        {/* Upcoming Matches Tab */}
        {activeTab === 'upcoming' && (
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Upcoming Matches</h2>
            {upcomingMatches.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No upcoming matches</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingMatches.map((match) => (
                  <MatchCard key={match.match_id} match={match} userId={user?.user_id} duesBlocked={totalDue > duesThreshold} pendingDuesAmount={totalDue} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Past Games Tab */}
        {activeTab === 'past' && (
          <div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Past Games</h2>
            {pastMatches.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No past games yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pastMatches.map((match) => (
                  <PastMatchCard 
                    key={match.match_id} 
                    match={match} 
                    userId={user?.user_id}
                    payment={getMatchPayment(match.match_id)}
                    onNavigate={() => navigate(`/match/${match.match_id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pending Payments - Only show on Upcoming tab */}
        {activeTab === 'upcoming' && pendingPayments.length > 0 && (
          <AggregatePaymentCard payments={pendingPayments} walletBalance={balance} />
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex justify-around items-center z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <NavButton icon={<Calendar />} label="Matches" active onClick={() => {}} />
        <NavButton icon={<User />} label="Profile" onClick={() => navigate('/profile')} />
        <AddToHomeScreen />
        {user?.is_admin && (
          <NavButton icon={<ShieldAlert />} label="Admin" onClick={() => navigate('/admin')} />
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, subtitle }) {
  return (
    <motion.div 
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"
      whileHover={{ y: -2 }}
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-[#E63946]/10 rounded-lg">{icon}</div>
        <div>
          <p className="text-sm text-slate-600">{label}</p>
          <p className="text-2xl font-bold text-[#0F172A]">{value}</p>
          {subtitle && <p className="text-xs text-green-600">{subtitle}</p>}
        </div>
      </div>
    </motion.div>
  );
}

function MatchCard({ match, userId, duesBlocked, pendingDuesAmount }) {
  const navigate = useNavigate();
  const [rsvpStatus, setRsvpStatus] = useState(match.user_rsvp_status || null);
  const [loading, setLoading] = useState(false);
  const [breakfastClaim, setBreakfastClaim] = useState(match.my_breakfast_claim || null);
  const [breakfastAmount, setBreakfastAmount] = useState('');
  const [breakfastEditing, setBreakfastEditing] = useState(false);
  const [breakfastSubmitting, setBreakfastSubmitting] = useState(false);
  const isSelected = match.selected_players?.includes(userId);
  const isFull = match.selected_players?.length >= match.max_players;

  // Update local state when match prop changes
  useEffect(() => {
    setRsvpStatus(match.user_rsvp_status || null);
  }, [match.user_rsvp_status]);

  useEffect(() => {
    setBreakfastClaim(match.my_breakfast_claim || null);
  }, [match.my_breakfast_claim]);

  const handleRSVP = async (e, status) => {
    e.stopPropagation(); // Prevent card click
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/rsvps`,
        { match_id: match.match_id, status },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      setRsvpStatus(status);
      toast.success(`RSVP updated to ${status.toUpperCase()}`);
      // Refresh page after a short delay
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'RSVP failed');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/match/${match.match_id}`;
    const matchDate = format(new Date(match.date), 'dd MMM');
    const matchType = match.match_type === 'saturday' ? 'Saturday' : 'Sunday';
    const shareText = `Join us for ${matchType} Cricket on ${matchDate}! RSVP now:`;
    
    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Weakenders ${matchType} Match`,
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fallback to clipboard
      }
    }
    
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const isWaitlisted = rsvpStatus === 'in' && !isSelected && isFull;

  const handleSubmitBreakfastClaim = async (e) => {
    e.stopPropagation();
    const amount = parseFloat(breakfastAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setBreakfastSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/matches/${match.match_id}/breakfast-claim`,
        { amount },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      setBreakfastClaim(res.data);
      setBreakfastEditing(false);
      setBreakfastAmount('');
      toast.success('Breakfast cost submitted for verification!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit breakfast cost');
    } finally {
      setBreakfastSubmitting(false);
    }
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
      whileHover={{ y: -2 }}
      data-testid={`match-${match.match_id}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 cursor-pointer" onClick={() => navigate(`/match/${match.match_id}`)}>
            <h3 className="text-xl font-bold text-[#0F172A] uppercase">
              {match.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {format(new Date(match.date), 'EEEE, dd MMM yyyy')}
            </p>
            {match.venue && <p className="text-sm text-slate-500">{match.venue}</p>}
            {match.opponent && (
              <p className="text-sm font-semibold text-[#E63946] mt-1">vs {match.opponent}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10"
              data-testid="share-match-btn"
            >
              <Share2 className="w-5 h-5" />
            </Button>
            {isSelected && (
              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full">
                SELECTED
              </span>
            )}
            {isWaitlisted && (
              <span className="px-3 py-1 bg-slate-100 text-slate-800 text-xs font-bold rounded-full">
                WAITLIST
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-600">
              <span className="font-bold text-[#0F172A]">{match.selected_players?.length || 0}</span>/{match.max_players} players
            </span>
          </div>
          {match.cost && (
            <span className="text-lg font-bold text-[#E63946]">₹{match.cost}</span>
          )}
        </div>

        {/* RSVP Toggle */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
          <p className="text-sm font-semibold text-slate-700">Your RSVP:</p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={rsvpStatus === 'out' ? 'default' : 'outline'}
              className={`text-xs font-bold ${
                rsvpStatus === 'out' 
                  ? 'bg-slate-700 hover:bg-slate-800 text-white' 
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50 hover:!text-slate-900'
              }`}
              onClick={(e) => handleRSVP(e, 'out')}
              disabled={loading}
              data-testid="rsvp-out-btn"
            >
              OUT
            </Button>
            <Button
              size="sm"
              variant={rsvpStatus === 'in' ? 'default' : 'outline'}
              className={`text-xs font-bold ${
                rsvpStatus === 'in' 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50 hover:!text-slate-900'
              }`}
              onClick={(e) => handleRSVP(e, 'in')}
              disabled={loading || duesBlocked}
              title={duesBlocked ? `Clear dues to RSVP` : ''}
              data-testid="rsvp-in-btn"
            >
              IN
            </Button>
          </div>
        </div>

        {/* Dues Blocking Warning */}
        {duesBlocked && (
          <div
            className="mt-2 flex items-center justify-between gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            onClick={(e) => e.stopPropagation()}
            data-testid="rsvp-dues-blocked-warning"
          >
            <p className="text-xs text-red-700">
              You owe <span className="font-bold">₹{pendingDuesAmount.toFixed(0)}</span>. Clear dues to RSVP IN.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); navigate('/payments'); }}
              className="text-xs h-7 border-red-400 text-red-600 hover:bg-red-500 hover:text-white flex-shrink-0"
              data-testid="rsvp-blocked-pay-now-btn"
            >
              Pay Now
            </Button>
          </div>
        )}

        {/* Breakfast Cost Self-Entry (Sunday matches, selected players, before cost is finalized) */}
        {match.match_type === 'sunday' && isSelected && !match.cost && (
          <div className="mt-3 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
            {breakfastClaim && !breakfastEditing ? (
              <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2" data-testid="breakfast-claim-status">
                <p className="text-sm text-amber-800 flex items-center gap-1.5">
                  <Coffee className="w-4 h-4" />
                  Breakfast: <span className="font-bold">₹{breakfastClaim.amount}</span>
                  {breakfastClaim.status === 'approved' ? (
                    <span className="text-green-600 font-semibold ml-1">✓ Verified</span>
                  ) : breakfastClaim.status === 'rejected' ? (
                    <span className="text-red-500 font-semibold ml-1">Rejected</span>
                  ) : (
                    <span className="text-amber-600 ml-1">(pending verification)</span>
                  )}
                </p>
                {breakfastClaim.status !== 'approved' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setBreakfastEditing(true); setBreakfastAmount(breakfastClaim.amount.toString()); }}
                    className="text-xs text-slate-500 hover:text-[#E63946] h-7"
                    data-testid="edit-breakfast-claim-btn"
                  >
                    Edit
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-xs text-slate-600 flex items-center gap-1 flex-1">
                  <Coffee className="w-4 h-4 text-amber-500" /> Paid for breakfast?
                </p>
                <Input
                  type="number"
                  placeholder="₹ Amount"
                  value={breakfastAmount}
                  onChange={(e) => setBreakfastAmount(e.target.value)}
                  className="w-24 h-8 text-sm"
                  data-testid="breakfast-amount-input"
                />
                <Button
                  size="sm"
                  onClick={handleSubmitBreakfastClaim}
                  disabled={breakfastSubmitting}
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-8"
                  data-testid="submit-breakfast-claim-btn"
                >
                  Submit
                </Button>
                {breakfastEditing && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); setBreakfastEditing(false); }}
                    className="text-xs h-8"
                    data-testid="cancel-breakfast-claim-btn"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function PastMatchCard({ match, userId, payment, onNavigate }) {
  const isSelected = match.selected_players?.includes(userId);
  const wasIn = match.user_rsvp_status === 'in';
  const result = match.result;
  
  const getPaymentStatus = () => {
    if (!payment) return null;
    if (payment.status === 'completed') return { text: 'Paid', color: 'bg-green-100 text-green-800' };
    if (payment.status === 'paid_pending_verification') return { text: 'Pending Verification', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Pending', color: 'bg-red-100 text-red-800' };
  };

  const getWinnerLabel = () => {
    if (!result?.winner) return null;
    if (result.winner === 'team_a') {
      return match.match_type === 'sunday' ? 'Bhandball Won' : 'Weakenders Won';
    }
    if (result.winner === 'team_b') {
      return match.match_type === 'sunday' ? 'Kacche Khiladi Won' : `${match.opponent || 'Opponent'} Won`;
    }
    if (result.winner === 'tie') return 'Match Tied';
    if (result.winner === 'no_result') return 'No Result';
    return null;
  };

  const paymentStatus = getPaymentStatus();
  const winnerLabel = getWinnerLabel();

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
      whileHover={{ y: -2 }}
      onClick={onNavigate}
      data-testid={`past-match-${match.match_id}`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-[#0F172A] uppercase">
              {match.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {format(new Date(match.date), 'EEEE, dd MMM yyyy')}
            </p>
            {match.venue && <p className="text-sm text-slate-500">{match.venue}</p>}
            {match.opponent && (
              <p className="text-sm font-semibold text-[#E63946] mt-1">vs {match.opponent}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {isSelected && (
              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                PLAYED
              </span>
            )}
            {wasIn && !isSelected && (
              <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-bold rounded-full">
                WAITLIST
              </span>
            )}
            {!wasIn && (
              <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs font-bold rounded-full">
                SKIPPED
              </span>
            )}
          </div>
        </div>

        {/* Result Summary */}
        {winnerLabel && (
          <div className="flex items-center gap-2 mb-3 py-2 px-3 bg-[#0F172A]/5 rounded-lg">
            <Trophy className="w-4 h-4 text-[#E63946]" />
            <span className="text-sm font-semibold text-[#0F172A]">{winnerLabel}</span>
            {result?.man_of_match_user && (
              <span className="ml-auto text-xs text-slate-600 flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-500" />
                {result.man_of_match_user.nickname || result.man_of_match_user.name}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-600">
              <span className="font-bold text-[#0F172A]">{match.selected_players?.length || 0}</span> players
            </span>
            {match.cost && (
              <span className="text-slate-600">
                Total: <span className="font-bold text-[#0F172A]">₹{match.cost}</span>
              </span>
            )}
          </div>
          {paymentStatus && (
            <span className={`px-3 py-1 text-xs font-bold rounded-full ${paymentStatus.color}`}>
              {paymentStatus.text}: ₹{payment.amount.toFixed(0)}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function AggregatePaymentCard({ payments, walletBalance = 0 }) {
  const navigate = useNavigate();
  const totalDue = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
  
  // Group payments by status
  const pendingVerification = payments.filter(p => p.status === 'paid_pending_verification');
  const pending = payments.filter(p => p.status === 'pending');
  
  return (
    <motion.div
      className="bg-gradient-to-br from-[#E63946] to-[#DC2626] rounded-xl shadow-lg p-6 text-white"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="aggregate-payment-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <IndianRupee className="w-6 h-6" />
          <h2 className="text-lg font-bold">Total Dues</h2>
        </div>
        <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold">
          {payments.length} {payments.length === 1 ? 'match' : 'matches'}
        </span>
      </div>

      <div className="text-center mb-4">
        <p className="text-5xl font-bold">₹{totalDue.toFixed(0)}</p>
        {pendingVerification.length > 0 && (
          <p className="text-sm text-white/80 mt-1">
            (₹{pendingVerification.reduce((sum, p) => sum + p.amount, 0).toFixed(0)} pending verification)
          </p>
        )}
      </div>

      {/* Wallet Balance Display */}
      {walletBalance > 0 && (
        <div className="bg-green-500/30 rounded-lg p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            <span className="text-sm font-semibold">Wallet Balance</span>
          </div>
          <span className="text-lg font-bold">₹{walletBalance.toFixed(0)}</span>
        </div>
      )}

      {/* Breakdown */}
      <div className="bg-white/10 rounded-lg p-3 mb-4 space-y-2">
        {payments.slice(0, 3).map((payment) => (
          <div key={payment.payment_id} className="flex items-center justify-between text-sm">
            <span className="text-white/80">
              {payment.match?.match_type === 'saturday' ? 'Sat' : 'Sun'} - {payment.match?.date && format(new Date(payment.match.date), 'dd MMM')}
            </span>
            <div className="flex items-center gap-2">
              <span className="font-bold">₹{payment.amount.toFixed(0)}</span>
              {payment.status === 'paid_pending_verification' && (
                <span className="px-1.5 py-0.5 bg-yellow-500/30 rounded text-xs">Verifying</span>
              )}
            </div>
          </div>
        ))}
        {payments.length > 3 && (
          <p className="text-xs text-white/60 text-center pt-1">
            +{payments.length - 3} more
          </p>
        )}
      </div>

      {/* Pay Button */}
      {pending.length > 0 && (
        <Button
          onClick={() => navigate('/payments')}
          className="w-full bg-white text-[#E63946] hover:bg-white/90 font-bold uppercase py-6 rounded-lg"
          data-testid="pay-all-btn"
        >
          <IndianRupee className="w-5 h-5 mr-2" />
          Pay Now
        </Button>
      )}

      {pending.length === 0 && pendingVerification.length > 0 && (
        <div className="text-center py-2 bg-white/10 rounded-lg">
          <p className="text-sm">All payments submitted - awaiting verification</p>
        </div>
      )}
    </motion.div>
  );
}

function PaymentCard({ payment }) {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-testid={`payment-${payment.payment_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-[#0F172A]">
            {payment.match?.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
          </p>
          <p className="text-sm text-slate-600">
            {payment.match?.date && format(new Date(payment.match.date), 'dd MMM yyyy')}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-[#E63946]">₹{payment.amount.toFixed(0)}</p>
          <Button
            size="sm"
            onClick={() => navigate(`/payment/${payment.payment_id}`)}
            className="mt-2 bg-[#E63946] hover:bg-[#DC2626] text-white font-bold uppercase text-xs"
            data-testid="pay-now-btn"
          >
            Pay Now
          </Button>
        </div>
      </div>
    </div>
  );
}

function NavButton({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
        active ? 'text-[#E63946]' : 'text-slate-600 hover:text-[#E63946]'
      }`}
      data-testid={`nav-${label.toLowerCase()}`}
    >
      <div className="w-6 h-6">{icon}</div>
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}

export default Dashboard;